(() => {
  const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
  const maxBytes = 15 * 1024 * 1024;

  const initialize = (root) => {
    if (root.dataset.sfkArtReady === "true") return;
    root.dataset.sfkArtReady = "true";

    const form = root.querySelector("[data-sfk-art-form]");
    const input = root.querySelector("[data-sfk-art-file]");
    const zone = root.querySelector("[data-sfk-art-upload]");
    const uploadCopy = root.querySelector("[data-sfk-art-upload-copy]");
    const uploadChange = root.querySelector("[data-sfk-art-upload-change]");
    const fileName = root.querySelector("[data-sfk-art-file-name]");
    const dropHint = root.querySelector("[data-sfk-art-drop-hint]");
    const fileError = root.querySelector("[data-sfk-art-file-error]");
    const editor = root.querySelector("[data-sfk-art-editor]");
    const editorEmpty = root.querySelector("[data-sfk-art-editor-empty]");
    const stage = root.querySelector("[data-sfk-art-stage]");
    const preview = root.querySelector("[data-sfk-art-preview]");
    const fine = root.querySelector("[data-sfk-art-fine]");
    const zoom = root.querySelector("[data-sfk-art-zoom]");
    const angle = root.querySelector("[data-sfk-art-angle]");
    const rotationValue = root.querySelector("[data-sfk-art-rotation]");
    const offsetXValue = root.querySelector("[data-sfk-art-offset-x]");
    const offsetYValue = root.querySelector("[data-sfk-art-offset-y]");
    const zoomValue = root.querySelector("[data-sfk-art-zoom-value]");
    const submit = root.querySelector("[data-sfk-art-submit]");
    const submitLabel = root.querySelector("[data-sfk-art-submit-label]");
    const formStatus = root.querySelector("[data-sfk-art-form-status]");
    const resetButton = root.querySelector("[data-sfk-art-reset]");
    const uploadToken = root.querySelector("[data-sfk-art-upload-token]");

    if (
      !form ||
      !input ||
      !zone ||
      !editor ||
      !stage ||
      !preview ||
      !fine ||
      !zoom ||
      !angle ||
      !uploadToken
    ) {
      return;
    }

    const state = {
      baseRotation: 0,
      fineRotation: 0,
      offsetX: 0,
      offsetY: 0,
      zoom: 1,
    };
    let objectUrl = "";
    let previewGeneration = 0;
    let drag = null;
    let submitting = false;
    let hasUnsubmittedSelection = false;
    let uploadTokenReady = false;
    let uploadTokenLoadedAt = 0;
    let uploadTokenPromise = null;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const formatBytes = (bytes) => {
      if (bytes >= 1024 * 1024) {
        return `${(bytes / 1024 / 1024).toLocaleString("de-DE", {
          maximumFractionDigits: 1,
        })} MB`;
      }
      return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("de-DE")} KB`;
    };

    const showEditor = (file) => {
      editor.hidden = false;
      if (editorEmpty) editorEmpty.hidden = true;
      if (fileName && file) {
        fileName.textContent = `${file.name} · ${formatBytes(file.size)}`;
      }
      uploadCopy.hidden = true;
      uploadChange.hidden = false;
    };

    const hideEditor = () => {
      editor.hidden = true;
      if (editorEmpty) editorEmpty.hidden = false;
      uploadCopy.hidden = false;
      uploadChange.hidden = true;
    };

    const normalizedRotation = () => {
      let value = state.baseRotation + state.fineRotation;
      while (value > 180) value -= 360;
      while (value < -180) value += 360;
      return Math.round(value * 2) / 2;
    };

    const render = () => {
      const rotation = normalizedRotation();
      preview.style.transform = `translate3d(${state.offsetX}%, ${state.offsetY}%, 0) rotate(${rotation}deg) scale(${state.zoom})`;
      rotationValue.value = String(rotation);
      offsetXValue.value = state.offsetX.toFixed(2);
      offsetYValue.value = state.offsetY.toFixed(2);
      zoomValue.value = state.zoom.toFixed(2);
      angle.textContent = `${rotation.toLocaleString("de-DE", {
        maximumFractionDigits: 1,
      })}°`;
    };

    const reset = () => {
      Object.assign(state, {
        baseRotation: 0,
        fineRotation: 0,
        offsetX: 0,
        offsetY: 0,
        zoom: 1,
      });
      fine.value = "0";
      zoom.value = "1";
      render();
    };

    const showFileError = (message) => {
      fileError.textContent = message;
      fileError.hidden = false;
      input.setAttribute("aria-invalid", "true");
    };

    const clearFileError = () => {
      fileError.textContent = "";
      fileError.hidden = true;
      input.removeAttribute("aria-invalid");
    };

    const showUploadTokenError = () => {
      uploadToken.value = "";
      uploadTokenReady = false;
      formStatus.textContent =
        "Das sichere Upload-Formular konnte nicht vorbereitet werden. Bitte lade die Seite neu.";
      formStatus.hidden = false;
    };

    const loadUploadToken = () => {
      if (uploadTokenPromise) return uploadTokenPromise;
      uploadTokenReady = false;
      uploadTokenPromise = fetch("/apps/kinderkunst/form-token/", {
        credentials: "omit",
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Token request failed: ${response.status}`);
          }
          return response.json();
        })
        .then((payload) => {
          if (typeof payload.upload_token !== "string" || !payload.upload_token) {
            throw new Error("Token response is incomplete");
          }
          uploadToken.value = payload.upload_token;
          uploadTokenLoadedAt = Date.now();
          uploadTokenReady = true;
        })
        .finally(() => {
          uploadTokenPromise = null;
        });
      return uploadTokenPromise;
    };

    loadUploadToken().catch(showUploadTokenError);

    const releaseObjectUrl = (url = objectUrl) => {
      if (!url) return;
      URL.revokeObjectURL(url);
      if (url === objectUrl) objectUrl = "";
    };

    const clearPreview = () => {
      previewGeneration += 1;
      preview.onload = null;
      preview.onerror = null;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = "";
      preview.removeAttribute("src");
      hideEditor();
      hasUnsubmittedSelection = false;
    };

    input.addEventListener("change", () => {
      clearFileError();
      const file = input.files && input.files[0];
      if (!file) {
        clearPreview();
        return;
      }
      if (!allowedTypes.has(file.type)) {
        input.value = "";
        clearPreview();
        showFileError("Bitte lade ein JPG-, PNG- oder WebP-Bild hoch.");
        return;
      }
      if (file.size > maxBytes) {
        input.value = "";
        clearPreview();
        showFileError("Das Bild darf höchstens 15 MB groß sein.");
        return;
      }

      clearPreview();
      const generation = previewGeneration;
      const nextObjectUrl = URL.createObjectURL(file);
      objectUrl = nextObjectUrl;
      hasUnsubmittedSelection = true;
      preview.onload = () => {
        if (generation !== previewGeneration) {
          releaseObjectUrl(nextObjectUrl);
          return;
        }
        preview.onload = null;
        preview.onerror = null;
        if (preview.naturalWidth < 600 || preview.naturalHeight < 600) {
          input.value = "";
          clearPreview();
          showFileError(
            "Das Bild ist zu klein. Bitte wähle eine Datei mit mindestens 600 × 600 Pixeln."
          );
          input.focus();
          return;
        }
        releaseObjectUrl(nextObjectUrl);
        showEditor(file);
        reset();
        stage.focus({ preventScroll: true });
      };
      preview.onerror = () => {
        if (generation !== previewGeneration) {
          releaseObjectUrl(nextObjectUrl);
          return;
        }
        input.value = "";
        clearPreview();
        showFileError("Das Bild konnte nicht gelesen werden. Bitte wähle eine andere Datei.");
      };
      preview.src = nextObjectUrl;
    });

    // Die Fläche sieht aus wie eine Drop-Zone, also muss sie auch eine sein.
    const supportsDrop =
      typeof DataTransfer === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    const eventHasFiles = (event) =>
      Boolean(event.dataTransfer) &&
      Array.prototype.indexOf.call(event.dataTransfer.types || [], "Files") !== -1;

    // Ein Fehlwurf neben die Zone würde sonst die Seite verlassen und die
    // bereits getroffene Auswahl verwerfen.
    const preventFileNavigation = (event) => {
      if (eventHasFiles(event)) event.preventDefault();
    };

    if (supportsDrop && dropHint) {
      dropHint.hidden = false;

      document.addEventListener("dragover", preventFileNavigation);
      document.addEventListener("drop", preventFileNavigation);

      zone.addEventListener("dragenter", (event) => {
        if (!eventHasFiles(event)) return;
        event.preventDefault();
        zone.classList.add("is-dragover");
      });

      zone.addEventListener("dragover", (event) => {
        if (!eventHasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        zone.classList.add("is-dragover");
      });

      zone.addEventListener("dragleave", (event) => {
        if (zone.contains(event.relatedTarget)) return;
        zone.classList.remove("is-dragover");
      });

      zone.addEventListener("drop", (event) => {
        if (!eventHasFiles(event)) return;
        event.preventDefault();
        zone.classList.remove("is-dragover");
        const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
        if (!dropped) return;
        const transfer = new DataTransfer();
        transfer.items.add(dropped);
        input.files = transfer.files;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    root.querySelectorAll("[data-sfk-art-rotate]").forEach((button) => {
      button.addEventListener("click", () => {
        state.baseRotation += Number(button.dataset.sfkArtRotate || 0);
        render();
      });
    });

    resetButton.addEventListener("click", reset);
    fine.addEventListener("input", () => {
      state.fineRotation = Number(fine.value);
      render();
    });
    zoom.addEventListener("input", () => {
      state.zoom = Number(zoom.value);
      render();
    });

    stage.addEventListener("pointerdown", (event) => {
      drag = {
        x: event.clientX,
        y: event.clientY,
        offsetX: state.offsetX,
        offsetY: state.offsetY,
        width: stage.clientWidth,
        height: stage.clientHeight,
      };
      stage.setPointerCapture(event.pointerId);
      stage.classList.add("is-dragging");
    });

    stage.addEventListener("pointermove", (event) => {
      if (!drag) return;
      state.offsetX = clamp(
        drag.offsetX + ((event.clientX - drag.x) / drag.width) * 100,
        -6,
        6
      );
      state.offsetY = clamp(
        drag.offsetY + ((event.clientY - drag.y) / drag.height) * 100,
        -6,
        6
      );
      render();
    });

    const stopDrag = () => {
      drag = null;
      stage.classList.remove("is-dragging");
    };
    stage.addEventListener("pointerup", stopDrag);
    stage.addEventListener("pointercancel", stopDrag);

    stage.addEventListener("keydown", (event) => {
      const movement = event.shiftKey ? 1 : 0.25;
      const moves = {
        ArrowLeft: [-movement, 0],
        ArrowRight: [movement, 0],
        ArrowUp: [0, -movement],
        ArrowDown: [0, movement],
      };
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      state.offsetX = clamp(state.offsetX + move[0], -6, 6);
      state.offsetY = clamp(state.offsetY + move[1], -6, 6);
      render();
    });

    form.addEventListener("submit", (event) => {
      clearFileError();
      const tokenIsFresh =
        uploadTokenReady && Date.now() - uploadTokenLoadedAt < 8 * 60 * 1000;
      if (!tokenIsFresh) {
        event.preventDefault();
        formStatus.textContent =
          "Das sichere Upload-Formular wird noch vorbereitet. Bitte versuche es gleich erneut.";
        formStatus.hidden = false;
        loadUploadToken()
          .then(() => form.requestSubmit())
          .catch(showUploadTokenError);
        return;
      }
      if (!input.files || !input.files[0]) {
        event.preventDefault();
        showFileError("Bitte wähle zuerst eine Zeichnung aus.");
        input.focus();
        return;
      }
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        return;
      }
      if (submitting) {
        event.preventDefault();
        return;
      }
      submitting = true;
      hasUnsubmittedSelection = false;
      form.setAttribute("aria-busy", "true");
      submit.disabled = true;
      submitLabel.textContent = "Upload läuft…";
      formStatus.textContent = "Bitte lass diese Seite geöffnet, bis der Upload bestätigt wurde.";
      formStatus.hidden = false;
    });

    const handleBeforeUnload = (event) => {
      if (!hasUnsubmittedSelection || submitting) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handleResize = () => render();
    window.addEventListener("resize", handleResize, { passive: true });

    const handleSectionUnload = (event) => {
      if (event.target !== root && !event.target.contains(root)) return;
      clearPreview();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("dragover", preventFileNavigation);
      document.removeEventListener("drop", preventFileNavigation);
      document.removeEventListener("shopify:section:unload", handleSectionUnload);
    };
    document.addEventListener("shopify:section:unload", handleSectionUnload);
  };

  document.querySelectorAll("[data-sfk-art-generator]").forEach(initialize);

  document.addEventListener("shopify:section:load", (event) => {
    event.target
      .querySelectorAll("[data-sfk-art-generator]")
      .forEach(initialize);
  });
})();
